import { useMemo } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMAvatar, PMButton, PMEmptyState, PMFilterBar, PMIcon, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { levelDef } from "@/lib/levels";
import { Page } from "@/ui/Page";
import { Kpi } from "@/ui/Kpi";
import { Character } from "@/ui/Character";
import { avatarColor, relTime } from "@/ui/format";
import { useClientRows, type ClientRow } from "./queries";
import "./clients.css";

// Client portfolio (core-features: Client portfolio & Paper Trail). Every
// client with the farms and fields they own, when they were last seen, and
// what's still open for them.
export function ClientsPage() {
  const { teamId } = useParams({ from: "/t/$teamId/clients" });
  const search = useSearch({ from: "/t/$teamId/clients" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const { data, totals, isLoading } = useClientRows();
  const q = (search.q || "").trim().toLowerCase();
  const rows = useMemo(() => data.filter((c) => !q || `${c.name} ${c.role} ${c.email} ${c.locations.map((l) => l.label).join(" ")}`.toLowerCase().includes(q)), [data, q]);
  const top = hierarchy.levels[0];

  return (
    <Page
      chip="MAPS & DATA"
      title="Client portfolio"
      subtitle={`${data.length} ${data.length === 1 ? "client" : "clients"} · ${totals.locations} linked ${totals.locations === 1 ? "location" : "locations"}`}
      actions={<PMButton variant="outline" icon="document" onClick={() => nav({ to: "/t/$teamId/clients/paper-trail", params: { teamId } })}>Paper Trail</PMButton>}
      wide
    >
      <div className="kpis">
        <Kpi emoji="👤" tone="tone-brand" value={data.length} label="Clients" />
        <Kpi emoji={top.emoji} tone="tone-text" value={totals.locations} label="Linked locations" />
        <Kpi emoji="📋" tone="tone-photo" value={totals.visits30} label="Visits, last 30 days" />
        <Kpi emoji="✅" tone="tone-warn" value={totals.openTasks} label="Open tasks" />
      </div>

      <PMFilterBar search={search.q || ""} searchPlaceholder={`Search clients, roles, ${top.plural.toLowerCase()}`}
        onSearch={(v: string) => nav({ to: ".", search: { q: v || undefined }, replace: true } as any)} />

      {isLoading && !data.length ? (
        <div className="client-grid">{[0, 1, 2, 3, 4, 5].map((i) => <PMSkeleton key={i} shape="rect" height={210} radius={18} />)}</div>
      ) : !data.length ? (
        <Character name="farmer" size={110} title="No clients yet" line="Client contacts added on the phone or in classic appear here with their farms." />
      ) : !rows.length ? (
        <PMEmptyState title="No clients match" description="Try another search." action={<PMButton variant="text" onClick={() => nav({ to: ".", search: {}, replace: true } as any)}>Clear search</PMButton>} />
      ) : (
        <div className="client-grid">
          {rows.map((c) => <ClientCard key={c.id} c={c} onOpen={() => nav({ to: "/t/$teamId/clients/$clientId", params: { teamId, clientId: c.id } })} />)}
        </div>
      )}
    </Page>
  );
}

function ClientCard({ c, onOpen }: { c: ClientRow; onOpen: () => void }) {
  const { hierarchy } = useApp();
  const direct = c.locations.filter((l) => l.direct);
  const shown = direct.length ? direct : c.locations;
  const stale = c.lastVisit ? (Date.now() - new Date(c.lastVisit).getTime()) / 86_400_000 > 21 : true;
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <article className="client-card pm-lift" onClick={onOpen} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()} aria-label={c.name || "Client"}>
      <header className="client-head">
        <PMAvatar name={c.name || "?"} size={48} color={avatarColor(c.id)} />
        <div className="client-id">
          <div className="client-name">{c.name || "Unnamed client"}</div>
          <div className="hint">{c.role || "Client"}</div>
        </div>
        <div className="client-actions" onClick={stop}>
          {c.phone && <a className="client-act" href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} aria-label={`Call ${c.name}`} title={c.phone}><PMIcon name="phone-outline" size={18} /></a>}
          {c.email && <a className="client-act" href={`mailto:${c.email}`} aria-label={`Email ${c.name}`} title={c.email}><PMIcon name="chat-dots-outline" size={18} /></a>}
        </div>
      </header>

      <div className="client-locs">
        {shown.slice(0, 4).map((l) => <span key={l.id} className="loc-chip">{levelDef(l.level, hierarchy.key).emoji} {l.label}</span>)}
        {shown.length > 4 && <span className="loc-chip loc-chip--more">+{shown.length - 4}</span>}
        {!shown.length && <span className="hint">No linked locations</span>}
      </div>

      <footer className="client-foot">
        <span className={`client-stat ${stale ? "is-stale" : ""}`}><small>Last visit</small><strong>{c.lastVisit ? relTime(c.lastVisit) : "Never"}</strong></span>
        <span className="client-stat"><small>30 days</small><strong>{c.visits30} {c.visits30 === 1 ? "visit" : "visits"}</strong></span>
        <span className={`client-stat ${c.openTasks ? "is-open" : ""}`}><small>Open tasks</small><strong>{c.openTasks}</strong></span>
        <span className="client-go"><PMIcon name="arrow-right" size={18} /></span>
      </footer>
    </article>
  );
}
